import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, MessageSquare, Activity, TrendingUp, Loader2 } from 'lucide-react';
import StatCard from '../components/dashboard/StatCard';
import Table from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import { API_PATH, request } from '../config/api';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import styles from './AnalyticsPage.module.css';

// CSS-var-driven series colors (matches the token-based recharts fills used on Dashboard)
const TYPE_COLORS = [
    'var(--sidebar-active-bg)',
    'var(--info)',
    'var(--accent-500)',
    'var(--warning)',
    'var(--success-dark)',
    'var(--error)',
];

const AnalyticsPage = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        request(`${API_PATH}/analytics`)
            .then((res) => {
                if (!res.ok) return res.json().then((body) => Promise.reject(new Error(body?.error || 'Failed to load analytics')));
                return res.json();
            })
            .then((json) => {
                if (!cancelled && json) setData(json);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || 'Failed to load analytics');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <div className={styles.page}>
                <PageHeroHeader icon={BarChart3} title="Analytics" subtitle="Understand how your audience interacts with each event and activity." />
                <div className={styles.loadingBlock}>
                    <Loader2 size={28} className={styles.spin} />
                    <span>Loading analytics…</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <PageHeroHeader icon={BarChart3} title="Analytics" subtitle="Understand how your audience interacts with each event and activity." />
                <div className={styles.errorBanner}>{error}</div>
            </div>
        );
    }

    const summary = data?.summary || {};
    const weeklyResponses = data?.weeklyResponses || [];
    const activityTypeBreakdown = data?.activityTypeBreakdown || [];
    const topActivities = data?.topActivities || [];

    const totalEvents = summary.totalEvents ?? 0;
    const totalActivities = summary.totalActivities ?? 0;
    const totalResponses = summary.totalResponses ?? 0;
    const avgPerActivity = summary.avgResponsesPerActivity ?? 0;

    const maxWeekly = Math.max(1, ...weeklyResponses.map((d) => d.responses));

    const statCards = [
        {
            label: 'Avg. responses per activity',
            value: avgPerActivity.toLocaleString(),
            delta: totalActivities > 0 ? { direction: 'up', text: `${totalResponses} total responses` } : null,
            icon: <BarChart3 size={18} />,
        },
        {
            label: 'Total responses',
            value: totalResponses.toLocaleString(),
            delta: totalEvents > 0 ? { direction: 'up', text: `Across ${totalEvents} event${totalEvents === 1 ? '' : 's'}` } : null,
            icon: <MessageSquare size={18} />,
        },
        {
            label: 'Questions run',
            value: totalActivities.toLocaleString(),
            delta: null,
            icon: <Activity size={18} />,
        },
    ];

    const columns = [
        { key: 'eventName', header: 'Event' },
        { key: 'question', header: 'Question' },
        { key: 'type', header: 'Type' },
        { key: 'responses', header: 'Responses' },
        {
            key: 'engagement',
            header: 'Engagement',
            render: (row) => (
                <span className={styles.engagementCell}>
                    <span
                        className={styles.engagementBar}
                        style={{ width: `${row.engagement || 0}%` }}
                    />
                    {row.engagement != null ? `${row.engagement}%` : '—'}
                </span>
            ),
        },
    ];

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={BarChart3}
                title="Analytics"
                subtitle="Understand how your audience interacts with each event and activity."
                actions={<Badge variant="neutral" size="md" className={styles.periodBadge}>Last 7 days</Badge>}
            />

            <section className={styles.statsGrid}>
                {statCards.map((card) => (
                    <StatCard
                        key={card.label}
                        label={card.label}
                        value={card.value}
                        delta={card.delta}
                        icon={card.icon}
                    />
                ))}
                <div className={styles.trendCard}>
                    <TrendingUp size={18} className={styles.trendIcon} />
                    <div className={styles.trendValue}>
                        {totalActivities > 0 ? totalResponses : '—'}
                    </div>
                    <div className={styles.trendLabel}>Total responses (all time)</div>
                </div>
            </section>

            <section className={styles.bentoRow}>
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <div>
                            <h2 className={styles.chartTitle}>Weekly Responses</h2>
                            <p className={styles.chartSub}>Response volume over the past 7 days</p>
                        </div>
                        <div className={styles.chartLegend}>
                            <span className={styles.legendDot} style={{ background: 'var(--primary-500)' }} />
                            Responses
                        </div>
                    </div>
                    <div className={styles.barChart}>
                        {weeklyResponses.length === 0 ? (
                            <p className={styles.chartEmpty}>No responses in the last 7 days.</p>
                        ) : (
                            weeklyResponses.map((d) => (
                                <div key={d.day + (d.date || '')} className={styles.barGroup}>
                                    <div className={styles.barTrack}>
                                        <div
                                            className={styles.barFill}
                                            style={{ height: `${(d.responses / maxWeekly) * 100}%` }}
                                        />
                                    </div>
                                    <span className={styles.barLabel}>{d.day}</span>
                                    <span className={styles.barValue}>{d.responses}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className={styles.breakdownCard}>
                    <h2 className={styles.chartTitle}>Activity Types</h2>
                    <p className={styles.chartSub}>Distribution across all events</p>
                    <div className={styles.breakdownList}>
                        {activityTypeBreakdown.length === 0 ? (
                            <p className={styles.chartEmpty}>No activities yet.</p>
                        ) : (
                            activityTypeBreakdown.map((item, idx) => (
                                <div key={item.type} className={styles.breakdownItem}>
                                    <div className={styles.breakdownLeft}>
                                        <span
                                            className={styles.breakdownDot}
                                            style={{ background: TYPE_COLORS[idx % TYPE_COLORS.length] }}
                                        />
                                        <span className={styles.breakdownType}>{item.type}</span>
                                    </div>
                                    <div className={styles.breakdownRight}>
                                        <div className={styles.breakdownTrack}>
                                            <div
                                                className={styles.breakdownFill}
                                                style={{
                                                    width: `${item.pct}%`,
                                                    background: TYPE_COLORS[idx % TYPE_COLORS.length],
                                                }}
                                            />
                                        </div>
                                        <span className={styles.breakdownPct}>{item.pct}%</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div>
                        <h2 className={styles.sectionTitle}>Activity performance</h2>
                        <p className={styles.sectionHint}>
                            Drill into which questions drive the most engagement across your events.
                        </p>
                    </div>
                    <p className={styles.sectionHintPill}>Tip: compare response count and engagement together before acting.</p>
                </div>
                <Table
                    columns={columns}
                    rows={topActivities}
                    emptyMessage="Run a few live sessions to see detailed analytics here."
                />
            </section>
        </div>
    );
};

export default AnalyticsPage;
